/**
 * useTabPresence — BroadcastChannel-based same-resource detection across tabs
 * on a single device.
 *
 * Use case: Amanda opens an event edit form in Tab A, then accidentally opens
 * the same event in Tab B (clicked a link, hit "Edit" twice, etc.). With the
 * lock's same-user auto-takeover, Tab B would silently dispossess Tab A —
 * usually fine, but if Tab A had unsaved typing, that's annoying. Detecting
 * the duplicate BEFORE acquiring the lock lets us nudge the user back to
 * their original tab.
 *
 * Mount sequence on Tab B:
 *   1. Open BroadcastChannel scoped to (resource_type, resource_id).
 *   2. Send a "ping" with our session id.
 *   3. Wait 300ms for any "present" replies.
 *   4. If any reply arrives → otherTabPresent = true. Caller should NOT acquire
 *      the lock and should show a "switch to other tab" message instead.
 *   5. If no reply in 300ms → otherTabPresent stays false, isCheckingPresence
 *      flips to false. Caller proceeds with normal flow.
 *
 * While mounted (any tab), incoming pings get a "present" response AND a
 * title flash on the responding tab to help the user find it. The flash
 * stops automatically when the tab regains focus.
 *
 * Graceful degrade: if BroadcastChannel is unavailable (very old Safari),
 * isCheckingPresence flips to false immediately and otherTabPresent stays
 * false. Behavior is identical to "no presence detection" — the lock's
 * auto-takeover handles dispossession the same as before.
 *
 * Composition with the edit lock:
 *   - Presence = warm tab on this device (BroadcastChannel reachable).
 *   - Lock auto-takeover = cold ghost lock from a closed tab (no presence
 *     response possible, lock row still in DB).
 *   These cover complementary cases. Both are needed.
 */

import { useEffect, useRef, useState } from 'react';

interface UseTabPresenceResult {
  /** True if another tab on this device is currently editing the same resource. */
  otherTabPresent: boolean;
  /** True until presence detection has resolved (initial 300ms ping window). */
  isCheckingPresence: boolean;
}

const PRESENCE_TIMEOUT_MS = 300;
const TITLE_FLASH_INTERVAL_MS = 700;
const TITLE_FLASH_MAX_MS = 30_000;
const FLASH_LABEL = 'Already open here';

function generatePresenceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useTabPresence(
  resourceType: string,
  resourceId: string | number | null | undefined,
  enabled: boolean = true,
): UseTabPresenceResult {
  const [otherTabPresent, setOtherTabPresent] = useState<boolean>(false);
  const [isCheckingPresence, setIsCheckingPresence] = useState<boolean>(true);
  const presenceIdRef = useRef<string>(generatePresenceId());

  const idStr = resourceId != null ? String(resourceId) : '';
  const channelKey = idStr ? `firefleet:edit:${resourceType}:${idStr}` : '';

  useEffect(() => {
    if (!enabled || !channelKey) {
      setIsCheckingPresence(false);
      setOtherTabPresent(false);
      return;
    }

    if (typeof BroadcastChannel === 'undefined') {
      // Old Safari fallback: skip detection. Lock auto-takeover still protects us.
      setIsCheckingPresence(false);
      return;
    }

    let cancelled = false;
    let flashInterval: ReturnType<typeof setInterval> | null = null;
    let flashStopTimer: ReturnType<typeof setTimeout> | null = null;
    const presenceId = presenceIdRef.current;
    const channel = new BroadcastChannel(channelKey);

    const stopFlashing = () => {
      if (flashInterval) {
        clearInterval(flashInterval);
        flashInterval = null;
      }
      if (flashStopTimer) {
        clearTimeout(flashStopTimer);
        flashStopTimer = null;
      }
      if (originalTitle !== null) {
        document.title = originalTitle;
        originalTitle = null;
      }
    };

    let originalTitle: string | null = null;
    const startFlashing = () => {
      if (flashInterval) return; // already flashing
      originalTitle = document.title;
      let toggle = false;
      flashInterval = setInterval(() => {
        if (cancelled) return;
        toggle = !toggle;
        document.title = toggle ? `🔔 ${FLASH_LABEL}` : (originalTitle || '');
      }, TITLE_FLASH_INTERVAL_MS);
      // Safety: stop flashing after 30s regardless.
      flashStopTimer = setTimeout(stopFlashing, TITLE_FLASH_MAX_MS);
    };

    const onVisibility = () => { if (!document.hidden) stopFlashing(); };
    const onFocus = () => { stopFlashing(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    const onMessage = (e: MessageEvent) => {
      if (cancelled) return;
      const msg = e.data || {};
      // BroadcastChannel doesn't echo to the sender, but be defensive in case
      // a future polyfill or relay does.
      if (msg.from === presenceId) return;
      if (msg.type === 'ping') {
        // Another tab is asking who's here. Announce ourselves and flash the title.
        channel.postMessage({ type: 'present', from: presenceId });
        startFlashing();
      } else if (msg.type === 'present') {
        // Another tab confirmed they're already editing this resource.
        setOtherTabPresent(true);
        setIsCheckingPresence(false);
      }
    };

    channel.addEventListener('message', onMessage);
    channel.postMessage({ type: 'ping', from: presenceId });

    const checkTimer = setTimeout(() => {
      if (cancelled) return;
      setIsCheckingPresence(false);
    }, PRESENCE_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimeout(checkTimer);
      stopFlashing();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [channelKey, enabled]);

  return { otherTabPresent, isCheckingPresence };
}
